import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { usePresentationKeys } from '@/hooks/usePresentationKeys';
import { PresentationToolbar } from './PresentationToolbar';
import { InitiativePopover } from './InitiativePopover';
import { InitiativeCard } from './InitiativeCard';
import { ICON_MAP } from '@/components/modals/IconPicker';
import { formatMonthLabel } from '@/utils/monthLabel';
import type { Initiative } from '@/types';

interface PresentationOverlayProps {
  initiatives: Initiative[];
  onExit: () => void;
  viewMode?: 'weekly' | 'monthly' | 'quarterly';
}

// Matches the redesigned two-line InitiativeCard (see TimelineView.tsx) — source's overlay still
// has the pre-redesign single-line height (2.25) which would clip the card's second line.
const CARD_HEIGHT = 3;
const CARD_GAP = 0.5;
const ROW_PADDING = 0.75;
const BASE_COL_WIDTH = 200;       // px per occupied quarter column at 100% zoom
const BASE_EMPTY_COL_WIDTH = 80;  // px per empty quarter column at 100% zoom
const BASE_LABEL_COL_WIDTH = 220; // px for label column at 100% zoom
const DEFAULT_ZOOM = 0.8;

const QUARTER_GROUPS = [
  { label: 'Q1', months: ['Jan', 'Feb', 'Mar'] },
  { label: 'Q2', months: ['Apr', 'May', 'Jun'] },
  { label: 'Q3', months: ['Jul', 'Aug', 'Sep'] },
  { label: 'Q4', months: ['Oct', 'Nov', 'Dec'] },
];

export function PresentationOverlay({ initiatives, onExit, viewMode = 'monthly' }: PresentationOverlayProps) {
  const fc = useFieldConfig();
  const { products, quarters, classifications } = useAppStore();

  // Build subProductId -> productId map for reliable matching
  const subProductToProductId = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p) => {
      (p.subProducts || []).forEach((sp) => {
        map.set(sp.id, p.id);
      });
    });
    return map;
  }, [products]);

  // Only show products that have initiatives in the filtered set
  const visibleProducts = useMemo(() => {
    const productIdsWithInitiatives = new Set<string>();
    initiatives.forEach((init) => {
      const productId = subProductToProductId.get(init.subProductId);
      if (productId) productIdsWithInitiatives.add(productId);
    });
    return products.filter((p) => productIdsWithInitiatives.has(p.id));
  }, [products, initiatives, subProductToProductId]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const [popover, setPopover] = useState<{ initiative: Initiative; rect: DOMRect } | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-hide toolbar
  const resetHideTimer = useCallback(() => {
    setToolbarVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setToolbarVisible(false), 3000);
  }, []);

  useEffect(() => {
    resetHideTimer();
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [resetHideTimer]);

  // Mouse movement shows toolbar
  useEffect(() => {
    const handleMove = () => resetHideTimer();
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [resetHideTimer]);

  // Fullscreen handling
  useEffect(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    }
    const handleFsChange = () => {
      if (!document.fullscreenElement) onExit();
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [onExit]);

  // Navigation
  const totalSlides = visibleProducts.length;
  const goNext = useCallback(() => setCurrentSlide((s) => Math.min(s + 1, totalSlides - 1)), [totalSlides]);
  const goPrev = useCallback(() => setCurrentSlide((s) => Math.max(s - 1, 0)), []);
  const goTo = useCallback((i: number) => setCurrentSlide(Math.max(0, Math.min(i, totalSlides - 1))), [totalSlides]);

  // Zoom
  const zoomIn = useCallback(() => setZoomLevel((z) => Math.min(z + 0.1, 2)), []);
  const zoomOut = useCallback(() => setZoomLevel((z) => Math.max(z - 0.1, 0.5)), []);
  const zoomReset = useCallback(() => setZoomLevel(DEFAULT_ZOOM), []);

  // Keyboard
  usePresentationKeys({
    totalSlides,
    currentSlide,
    onNext: goNext,
    onPrev: goPrev,
    onGoTo: goTo,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onZoomReset: zoomReset,
    onExit,
    onActivity: resetHideTimer,
    enabled: true,
  });

  // Current product data — must be declared before occupancy memos that depend on productInitiatives
  const currentProduct = visibleProducts[currentSlide];
  const subProducts = currentProduct?.subProducts || [];
  const productInitiatives = useMemo(() => {
    const spIds = new Set(subProducts.map((sp) => sp.id));
    return initiatives.filter((init) => spIds.has(init.subProductId));
  }, [initiatives, subProducts]);

  // Computed column widths based on zoom
  const colWidth = BASE_COL_WIDTH * zoomLevel;
  const emptyColWidth = BASE_EMPTY_COL_WIDTH * zoomLevel;
  const labelColWidth = BASE_LABEL_COL_WIDTH * zoomLevel;

  // Which quarters have ≥1 initiative on this slide (scoped to current product)
  const occupiedQuarters = useMemo(() => {
    const occupied = new Set<string>();
    productInitiatives.forEach((init) => {
      const si = quarters.findIndex(q => q.key === init.startQuarter);
      if (si < 0) return;
      const rawEi = init.endQuarter ? quarters.findIndex(q => q.key === init.endQuarter) : si;
      const ei = rawEi < 0 ? si : rawEi;
      for (let idx = si; idx <= ei; idx++) occupied.add(quarters[idx].key);
    });
    return occupied;
  }, [productInitiatives, quarters]);

  // Pixel left-offset per quarter index, accounting for mixed column widths
  const quarterDisplayOffsets = useMemo(() => {
    const offsets = new Map<number, number>();
    let x = 0;
    quarters.forEach((q, i) => {
      offsets.set(i, x);
      x += occupiedQuarters.has(q.key) ? colWidth : emptyColWidth;
    });
    return offsets;
  }, [quarters, occupiedQuarters, colWidth, emptyColWidth]);

  // Grid template — per-column widths
  const gridCols = useMemo(() =>
    `${labelColWidth}px ${quarters.map(q => occupiedQuarters.has(q.key) ? `${colWidth}px` : `${emptyColWidth}px`).join(' ')}`,
  [labelColWidth, quarters, occupiedQuarters, colWidth, emptyColWidth]);

  // Quarterly superheader cells — occupancy-aware band colouring
  const quarterlyHeaderCells = useMemo(() => {
    type Cell = { key: string; label: string; colSpan: number; colorClass: string };
    const cells: Cell[] = [];
    let i = 0;
    while (i < quarters.length) {
      const [fyLabel, monthLabel] = quarters[i].fullLabel.split(' ');
      const qg = QUARTER_GROUPS.find(g => g.months.includes(monthLabel));
      if (!qg) { i++; continue; }
      let colSpan = 1;
      let bandHasInitiatives = occupiedQuarters.has(quarters[i].key);
      let j = i + 1;
      while (j < quarters.length) {
        const [nFy, nMonth] = quarters[j].fullLabel.split(' ');
        if (nFy !== fyLabel || !qg.months.includes(nMonth)) break;
        if (occupiedQuarters.has(quarters[j].key)) bandHasInitiatives = true;
        colSpan++;
        j++;
      }
      const qIdx = QUARTER_GROUPS.findIndex(g => g.label === qg.label);
      cells.push({
        key: `${fyLabel}-${qg.label}-${i}`,
        label: qg.label,
        colSpan,
        colorClass: bandHasInitiatives
          ? (qIdx % 2 === 0 ? 'bg-blue-100 text-blue-700' : 'bg-sky-200 text-sky-800')
          : 'bg-white text-gray-300',
      });
      i = j;
    }
    return cells;
  }, [quarters, occupiedQuarters]);

  // Group by sub-product
  const initiativesBySubProduct = useMemo(() => {
    const grouped = new Map<string, Initiative[]>();
    productInitiatives.forEach((init) => {
      if (!grouped.has(init.subProductId)) grouped.set(init.subProductId, []);
      grouped.get(init.subProductId)!.push(init);
    });
    grouped.forEach((inits) => {
      inits.sort((a, b) => {
        const aIdx = quarters.findIndex((q) => q.key === a.startQuarter);
        const bIdx = quarters.findIndex((q) => q.key === b.startQuarter);
        return (aIdx < 0 ? 9999 : aIdx) - (bIdx < 0 ? 9999 : bIdx);
      });
    });
    return grouped;
  }, [productInitiatives, quarters]);

  const getQuarterIndex = (key: string) => quarters.findIndex((q) => q.key === key);

  const calculateRowHeight = (laneCount: number) => {
    if (laneCount === 0) return 4;
    return ROW_PADDING + laneCount * (CARD_HEIGHT + CARD_GAP) + ROW_PADDING;
  };

  // Initiative click -> popover
  const handleInitiativeClick = (initiative: Initiative, e: React.MouseEvent) => {
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setPopover({ initiative, rect });
  };

  // Auto-scroll to leftmost initiative on slide change
  useEffect(() => {
    if (!scrollRef.current || productInitiatives.length === 0) return;
    const minIdx = productInitiatives.reduce((min, init) => {
      const idx = getQuarterIndex(init.startQuarter ?? '');
      return idx >= 0 && idx < min ? idx : min;
    }, quarters.length);
    const scrollLeft = Math.max(0, (quarterDisplayOffsets.get(minIdx) ?? minIdx * colWidth) - 40);
    scrollRef.current.scrollLeft = scrollLeft;
    scrollRef.current.scrollTop = 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  if (!currentProduct) return null;

  const overlay = (
    <div className="fixed inset-0 z-[10000] bg-gray-100 flex flex-col animate-presentation-in">
      {/* Toolbar */}
      <PresentationToolbar
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        productName={currentProduct.name}
        zoomLevel={zoomLevel}
        visible={toolbarVisible}
        onNext={goNext}
        onPrev={goPrev}
        onGoTo={goTo}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomReset={zoomReset}
        onExit={onExit}
      />

      {/* Main content area */}
      <div
        className="flex-1 overflow-hidden pt-20 pb-16 px-6"
        onClick={() => setPopover(null)}
      >
        <div
          ref={scrollRef}
          className="w-full h-full overflow-auto scrollbar-thin"
        >
          {/* Timeline grid - no transform, column widths are zoom-adjusted */}
          <div className="relative min-w-max">
            {/* Sticky header */}
            <div className="sticky top-0 z-20">
              {/* Quarter superheader (quarterly mode) */}
              {viewMode === 'quarterly' && (
                <div className="grid gap-0" style={{ gridTemplateColumns: gridCols }}>
                  <div
                    className="sticky left-0 z-30 bg-white border-b border-r border-gray-200"
                    style={{ width: `${labelColWidth}px` }}
                  />
                  {quarterlyHeaderCells.map((cell) => (
                    <div
                      key={cell.key}
                      className={`${cell.colorClass} border-b border-gray-200 border-l border-gray-200 py-1.5 text-center font-semibold text-xs tracking-wide`}
                      style={{ gridColumn: `span ${cell.colSpan}` }}
                    >
                      {cell.label}
                    </div>
                  ))}
                </div>
              )}

              {/* Month/quarter header */}
              <div className="grid gap-0" style={{ gridTemplateColumns: gridCols }}>
                <div
                  className="sticky left-0 z-30 bg-white border-b border-r border-gray-200 py-2.5 px-4 font-semibold text-gray-700 text-xs"
                  style={{ width: `${labelColWidth}px` }}
                >
                  {fc.label('product')} / {fc.label('subProduct')}
                </div>
                {quarters.map((q) => (
                  <div
                    key={q.id}
                    className={`bg-white border-b border-gray-200 border-l border-gray-200 py-2.5 px-1 text-center text-xs whitespace-nowrap overflow-hidden ${occupiedQuarters.has(q.key) ? 'font-semibold text-gray-700' : 'font-normal text-gray-300'}`}
                  >
                    {formatMonthLabel(q.fullLabel)}
                  </div>
                ))}
              </div>
            </div>

            {/* Product header row - inside grid, sticky left */}
            <div
              className="grid gap-0 bg-blue-50 border-b border-gray-200"
              style={{ gridTemplateColumns: gridCols }}
            >
              <div
                className="sticky left-0 z-10 bg-blue-50 py-2.5 px-4 font-semibold text-gray-800 text-sm border-r border-gray-200 flex items-center gap-2"
                style={{ width: `${labelColWidth}px` }}
              >
                {currentProduct.icon && ICON_MAP[currentProduct.icon] && (
                  <span style={{ color: currentProduct.iconColor || '#6B7280' }}>
                    {(() => {
                      const Ic = ICON_MAP[currentProduct.icon];
                      return <Ic className="w-4 h-4 flex-shrink-0" />;
                    })()}
                  </span>
                )}
                <span>{currentProduct.name}</span>
                <span className="text-xs font-normal text-gray-500">
                  · {productInitiatives.length}
                </span>
              </div>
              {quarters.map((q) => (
                <div key={q.id} className="bg-blue-50 border-l border-blue-100" />
              ))}
            </div>

            {/* Sub-product rows */}
            {subProducts.map((sp) => {
              const spInitiatives = (initiativesBySubProduct.get(sp.id) || []).filter(
                (init) => getQuarterIndex(init.startQuarter ?? '') >= 0,
              );
              if (spInitiatives.length === 0) return null;

              // Lane packing
              const laneAssignments = new Map<string, number>();
              const laneEndIndices: number[] = [];
              for (const init of spInitiatives) {
                const si = getQuarterIndex(init.startQuarter ?? '');
                const rawEi = init.endQuarter ? getQuarterIndex(init.endQuarter) : si;
                const ei = rawEi < 0 ? si : rawEi;
                const fitLane = laneEndIndices.findIndex((end) => si > end);
                if (fitLane === -1) {
                  laneAssignments.set(init.id, laneEndIndices.length);
                  laneEndIndices.push(ei);
                } else {
                  laneAssignments.set(init.id, fitLane);
                  laneEndIndices[fitLane] = ei;
                }
              }
              const laneCount = laneEndIndices.length;
              const rowHeight = calculateRowHeight(laneCount);

              return (
                <div
                  key={sp.id}
                  className="grid gap-0 border-b border-gray-100"
                  style={{ gridTemplateColumns: gridCols, height: `${rowHeight}rem` }}
                >
                  {/* Sub-product label - sticky left */}
                  <div
                    className="sticky left-0 z-10 bg-white p-3 pl-6 text-sm text-gray-600 font-medium border-r border-gray-200 flex items-start"
                    style={{ height: `${rowHeight}rem`, width: `${labelColWidth}px` }}
                  >
                    {sp.name}
                  </div>

                  {/* Initiative cards container */}
                  <div
                    className="relative"
                    style={{
                      gridColumn: `2 / span ${quarters.length}`,
                      height: `${rowHeight}rem`,
                    }}
                  >
                    {/* Column dividers */}
                    <div className="absolute inset-0 flex pointer-events-none">
                      {quarters.map((q) => (
                        <div
                          key={`bg-${q.id}`}
                          className="border-l border-gray-100"
                          style={{ width: occupiedQuarters.has(q.key) ? `${colWidth}px` : `${emptyColWidth}px`, flexShrink: 0 }}
                        />
                      ))}
                    </div>

                    {/* Cards */}
                    {spInitiatives.map((initiative) => {
                      const startIdx = getQuarterIndex(initiative.startQuarter ?? '');
                      const endIdx = initiative.endQuarter
                        ? getQuarterIndex(initiative.endQuarter)
                        : startIdx;
                      const validEnd = endIdx < 0 ? startIdx : endIdx;
                      const span = validEnd - startIdx + 1;
                      const leftPx = quarterDisplayOffsets.get(startIdx) ?? startIdx * colWidth;
                      const endOffsetPx = quarterDisplayOffsets.get(validEnd) ?? validEnd * colWidth;
                      const endColW = occupiedQuarters.has(quarters[validEnd]?.key) ? colWidth : emptyColWidth;
                      const widthPx = (endOffsetPx + endColW) - leftPx;
                      const laneIdx = laneAssignments.get(initiative.id) ?? 0;

                      return (
                        <div
                          key={initiative.id}
                          className="absolute box-border cursor-pointer"
                          style={{
                            left: `${leftPx}px`,
                            width: `${widthPx}px`,
                            top: `${ROW_PADDING + laneIdx * (CARD_HEIGHT + CARD_GAP)}rem`,
                            paddingLeft: '0.75rem',
                            paddingRight: '0.75rem',
                            height: `${CARD_HEIGHT}rem`,
                          }}
                          onClick={(e) => handleInitiativeClick(initiative, e)}
                        >
                          <InitiativeCard
                            initiative={initiative}
                            quarterSpan={span}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer legend */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[10001] bg-gray-900/90 backdrop-blur-sm border-t border-gray-700 px-6 py-3 transition-opacity duration-300 ${
          toolbarVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center justify-center gap-6 text-xs">
          <span className="font-medium text-gray-400 uppercase tracking-wider">Classification:</span>
          {classifications.map((c) => (
            <div key={c.id} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
              <span className="text-gray-300">{c.badgeLabel}</span>
            </div>
          ))}
          <div className="w-px h-4 bg-white/20" />
          <span className="text-gray-500">
            ← → Navigate · +/- Zoom · Esc Exit
          </span>
        </div>
      </div>

      {/* Popover */}
      {popover && (
        <InitiativePopover
          initiative={popover.initiative}
          anchorRect={popover.rect}
          onClose={() => setPopover(null)}
        />
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
